import { createSelector } from 'reselect'
import { GenericWorkout } from './../../types/generic-workout'
import {
  WorkoutResponse,
  ScheduledWorkoutResponse,
  CompletedWorkoutResponse,
} from '../actions'
import { RootState } from './../reducers/rootReducers'

const likedWorkoutState = (state: RootState) =>
  state.entities.likedWorkoutsReducer.likedWorkouts

const completedWorkoutState = (state: RootState) =>
  state.entities.completedWorkoutsReducer.completedWorkouts

const scheduledWorkoutState = (state: RootState) =>
  state.entities.scheduledWorkoutsReducer.scheduledWorkouts

const transformScheduledWorkout = (
  sw: ScheduledWorkoutResponse,
): GenericWorkout => ({
  id: sw.workout.id,
  title: sw.workout.title,
  description: sw.workout.description,
  trainerName: sw.workout.trainer
    ? `${sw.workout.trainer.firstName} ${sw.workout.trainer.lastName}`
    : '',
  length: sw.workout.length,
  thumbnail: sw.workout.thumbnail,
  type: sw.workout.type,
  date: sw.startDate,
})

const transformCompletedWorkout = (
  cw: CompletedWorkoutResponse,
): GenericWorkout => ({
  id: cw.id,
  title: cw.title,
  description: cw.description,
  trainerName: `${cw.firstName} ${cw.lastName}`,
  length: cw.duration,
  thumbnail: cw.thumbnail,
  type: cw.type,
  date: cw.createdAt,
})

const transformToGeneric = (w: WorkoutResponse): GenericWorkout => ({
  id: w.id,
  title: w.title,
  description: w.description,
  trainerName: w.trainer ? `${w.trainer.firstName} ${w.trainer.lastName}` : '',
  length: w.length,
  thumbnail: w.thumbnail,
  type: w.type,
})

export const likedWorkoutSelector = createSelector(
  likedWorkoutState,
  (likedWorkouts: WorkoutResponse[]): GenericWorkout[] => {
    if (likedWorkouts.length) {
      const generic = likedWorkouts.map(w => transformToGeneric(w))
      return generic
    }
    return []
  },
)

export const completedWorkoutSelector = createSelector(
  completedWorkoutState,
  (completedWorkouts: CompletedWorkoutResponse[]): GenericWorkout[] => {
    if (completedWorkouts.length) {
      const generic = completedWorkouts.map(cw => transformCompletedWorkout(cw))
      return generic
    }
    return []
  },
)

export const scheduledWorkoutSelector = createSelector(
  scheduledWorkoutState,
  (scheduledWorkout: ScheduledWorkoutResponse[]): GenericWorkout[] => {
    if (scheduledWorkout.length) {
      const filtered = scheduledWorkout.filter(sw => {
        const startDate = new Date(sw.startDate).getTime()
        const currDate = new Date().getTime()
        return currDate < startDate
      })
      const generic = filtered.map(sw => transformScheduledWorkout(sw))
      return generic
    }
    return []
  },
)
